import { FC, useEffect, useState } from 'react';
import { Button, IconButton, InlineNotification } from '@carbon/react';
import {
  CheckmarkFilled,
  CheckmarkOutline,
  CloseFilled,
  CloseOutline,
} from '@carbon/icons-react';
import { WellStatusFields, type WellStatusRow } from 'components/well-cards';
import { WorkspaceCard, type WellScope } from 'components/workspace-cards';
import {
  DATE_FIELD_LABELS,
  FIELD_TO_SERVER_KEY,
  type StagedWell,
} from 'types/well-workspace';
import { errorFor } from '../well-status-validation';
import { EditedPill } from '../edited-pill';
import { InlineField } from './inline-field';
import type { StatusAutosave } from '../hooks';
import styles from './well-status-card.module.scss';

/** The row labels, as the field grid draws them. */
const ROW_LABELS: Record<WellStatusRow, string> = {
  status: 'SRP Well Status',
  ...DATE_FIELD_LABELS,
};

interface Props {
  well: StagedWell;
  scope: WellScope;
  /** A promoted workspace is frozen: neither the edit nor the verdict can move. */
  promoted: boolean;
  /** The card's own editing: the draft, the gate and the debounced save. */
  autosave: StatusAutosave;
  /**
   * Opens the decision panel for a verdict the well does not already carry. Clicking the
   * verdict it DOES carry clears it instead — see `onClearVerdict`.
   */
  onSetVerdict: (verdict: 'correct' | 'incorrect') => void;
  /** Clears the well's current verdict outright, with no reason to capture. */
  onClearVerdict: () => void;
  /** True while a clear is in flight, so the pair cannot be double-fired. */
  verdictBusy: boolean;
}

/**
 * Well Status — the card Epic 2 makes editable (Story 2).
 *
 * The header carries the pencil and the Mark Correct / Mark Incorrect pair; the collapse and
 * pin controls after them belong to the card shell. Marking a well correct and promoting is
 * what freezes its Well Status against system ingest, which is why the two verdict controls
 * live on this card rather than in the Tasks panel.
 * @param {Props} props The scoped staged well, the stepper, and the edit/verdict callbacks.
 * @return {JSX.Element} The card.
 */
export const WellStatusCard: FC<Props> = ({
  well,
  scope,
  promoted,
  autosave,
  onSetVerdict,
  onClearVerdict,
  verdictBusy,
}) => {
  const edited = well.editedFields.length > 0;
  // Absent until the first status edit seeds the well's freeze item, so there is nothing
  // to post a verdict against on a workspace nobody has edited.
  const verdict = well.wellStatusVerdict;
  const current = verdict?.verdict ?? 'unanswered';

  /* The banner is dismissible per the mock, and comes back for the next WELL — not for
     the next edit. It used to be keyed on `editedFields` too, which was fine when one
     panel-Save produced one change; under autosave that list moves on nearly every
     commit, so the banner resurrected itself a second after being dismissed and could
     not be got rid of. The uwi alone is what "this well's staged edits" means. */
  const [dismissed, setDismissed] = useState(false);
  useEffect(() => setDismissed(false), [well.uwi]);

  // Clicking the verdict the well already carries clears it, with no reason to capture;
  // clicking the other one opens the decision panel. Mirrors the Inventory workspace's
  // header, which is the pattern this card's pair was asked to follow.
  const choose = (next: 'correct' | 'incorrect') => {
    if (current === next) onClearVerdict();
    else onSetVerdict(next);
  };

  // No freeze item yet means no verdict to set; a promoted workspace is frozen outright.
  const verdictsDisabled = promoted || verdictBusy || !verdict;
  const pendingHint = verdict ? '' : ' — edit the well status first';

  return (
    <WorkspaceCard
      title="Well Status"
      scope={scope}
      titleSuffix={edited ? <EditedPill /> : undefined}
      actions={
        <>
          {/* No pencil here: each row carries its own, which is what says THAT ROW can be
              changed. A card-level one would only have reopened the panel this card
              replaced. Drawn in the mock's order — correct, then incorrect — and always present:
              a pair that materialized only once a freeze item existed would read as the
              page rearranging itself. */}
          <IconButton
            kind="ghost"
            size="md"
            align="bottom-right"
            className={current === 'correct' ? styles.correct : undefined}
            label={
              current === 'correct'
                ? 'Remove Correct status'
                : `Mark as Correct${pendingHint}`
            }
            disabled={verdictsDisabled}
            onClick={() => choose('correct')}
          >
            {current === 'correct' ? <CheckmarkFilled /> : <CheckmarkOutline />}
          </IconButton>
          <IconButton
            kind="ghost"
            size="md"
            align="bottom-right"
            className={current === 'incorrect' ? styles.incorrect : undefined}
            label={
              current === 'incorrect'
                ? 'Remove Incorrect status'
                : `Mark as Incorrect${pendingHint}`
            }
            disabled={verdictsDisabled}
            onClick={() => choose('incorrect')}
          >
            {current === 'incorrect' ? <CloseFilled /> : <CloseOutline />}
          </IconButton>
        </>
      }
    >
      {edited && !promoted && !dismissed && (
        <InlineNotification
          kind="info"
          lowContrast
          className={styles.stagedNotice}
          title="Edits are staged"
          subtitle="Your edits are staged to the workspace when marked correct — they're saved to the wells when the workspace is promoted."
          onClose={() => {
            setDismissed(true);

            return false;
          }}
          onCloseButtonClick={() => setDismissed(true)}
        />
      )}
      {/* The rules the draft breaks, and the only way back out of them: a dropdown
          commits on pick, so one click can put the card here and the panel's Cancel is
          gone. Held, not failed — nothing has been sent. */}
      {autosave.errors.length > 0 && (
        /* The Revert sits BESIDE the notification, not inside it: Carbon's
           InlineNotification renders no children (it throws on them — only
           ActionableNotification takes an action), and this is not one of those. */
        <div className={styles.heldNotice}>
          <InlineNotification
            kind="warning"
            lowContrast
            hideCloseButton
            className={styles.stagedNotice}
            title="Finish this edit before it can be saved"
            subtitle={autosave.errors.map((error) => error.message).join('. ')}
          />
          <Button kind="ghost" size="sm" onClick={autosave.revert}>
            Revert
          </Button>
        </div>
      )}

      {/* A refusal the client-side mirror did not predict. The draft is kept, so the
          reader can correct it rather than retype it, and the next commit re-arms. */}
      {autosave.error && (
        <InlineNotification
          kind="error"
          lowContrast
          className={styles.stagedNotice}
          title="Could not save the well status"
          subtitle={autosave.error}
          onCloseButtonClick={autosave.dismissError}
        />
      )}

      {/* A 600ms-delayed save is otherwise invisible to a screen reader. */}
      <span className={styles.saveStatus} aria-live="polite">
        {autosave.saving ? 'Saving…' : ''}
      </span>

      <WellStatusFields
        well={autosave.values}
        editedFields={well.editedFields}
        editedMark={<EditedPill />}
        renderValue={(row, readOnly) => {
          // A row nobody can change renders exactly as the Well page renders it — no
          // pencil, nothing that invites a click the server would refuse.
          if (!autosave.editable(row)) return undefined;

          /* The edited wash — the field-level half of what the "Edited" pill says on the
             label. Either half of "differs from the well's own record" counts: already
             staged on the server, or committed here and not saved yet. */
          const serverKey =
            FIELD_TO_SERVER_KEY[row as keyof typeof FIELD_TO_SERVER_KEY];

          return (
            <InlineField
              // Remount per well so an open editor does not carry over to the next one.
              key={`${well.uwi}-${row}`}
              row={row}
              label={ROW_LABELS[row]}
              kind={row === 'status' ? 'select' : 'date'}
              value={autosave.values[row]}
              readOnly={readOnly}
              onCommit={(_row, next) => autosave.commit(row, next)}
              heldError={
                autosave.pending.size > 0
                  ? errorFor(autosave.errors, row)
                  : null
              }
              dirty={
                autosave.pending.has(row) ||
                (serverKey !== undefined &&
                  (well.userEditedFields ?? well.editedFields).includes(
                    serverKey
                  ))
              }
            />
          );
        }}
      />
    </WorkspaceCard>
  );
};
